import {
  useFinishPasskeyRegistrationMutation,
  useStartPasskeyRegistrationMutation,
} from '@/generated/graphql';
import { useLayoutStyles } from '@/hooks/styles';
import { graphQLErrorCode } from '@/utils/apollo-errors';
import { MFA_ERROR_CODES } from '@/utils/mfa-errors';
import {
  defaultPasskeyLabel,
  PASSKEY_LABEL_MAX_LENGTH,
  passkeyErrorMessage,
} from '@/utils/passkeys';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { startRegistration } from '@simplewebauthn/browser';
import React, { useEffect, useState } from 'react';

interface PasskeyRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The passkey is saved. `recoveryCodes` is set when it was the first
   * factor: show the codes once. Empty when the account already had codes.
   */
  onRegistered: (recoveryCodes: string[], label: string) => void;
  /**
   * The enroll-only sign-in ended (auth:mfa_challenge_invalid), so the
   * password must be entered again. Without it the dialog shows the error.
   */
  onSignInExpired?: (message: string) => void;
}

/**
 * Adds a passkey: the person names it, then the browser asks for a
 * fingerprint, face, screen lock or security key, and the API checks and
 * stores the new passkey.
 */
export default function PasskeyRegistrationDialog({
  open,
  onClose,
  onRegistered,
  onSignInExpired,
}: PasskeyRegistrationDialogProps): JSX.Element {
  const layoutClasses = useLayoutStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [startPasskeyRegistration] = useStartPasskeyRegistrationMutation();
  const [finishPasskeyRegistration] = useFinishPasskeyRegistrationMutation();
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(defaultPasskeyLabel(window.navigator.userAgent));
      setError('');
      setBusy(false);
    }
  }, [open]);

  async function register() {
    if (busy) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const { data } = await startPasskeyRegistration();
      const credential = await startRegistration({
        optionsJSON: data?.startPasskeyRegistration.options,
      });
      const { data: finished } = await finishPasskeyRegistration({
        variables: { input: { credential, label: label.trim() } },
        // The card behind the codes dialog catches up in the background.
        refetchQueries: ['ViewerMfa'],
      });
      const result = finished?.finishPasskeyRegistration;
      onRegistered(result?.recoveryCodes ?? [], result?.factor.label ?? '');
    } catch (err) {
      if (
        onSignInExpired &&
        graphQLErrorCode(err) === MFA_ERROR_CODES.challengeInvalid
      ) {
        onSignInExpired(passkeyErrorMessage(err));
        return;
      }

      setError(passkeyErrorMessage(err));
    }

    setBusy(false);
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="passkey-registration-dialog-title"
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
    >
      <div className={layoutClasses.progress}>{busy && <LinearProgress />}</div>
      <DialogTitle id="passkey-registration-dialog-title">
        Add a passkey
      </DialogTitle>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          register();
        }}
      >
        <DialogContent dividers>
          {Boolean(error) && (
            <Box mb={2}>
              <Alert severity="error" variant="outlined" role="alert">
                {error}
              </Alert>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" paragraph>
            With a passkey, you confirm it is you with your fingerprint, face,
            screen lock or a security key. A passkey works only on NomadMD, so a
            fake sign-in page cannot use it.
          </Typography>
          <Box mt={3}>
            <TextField
              id="passkey-name"
              label="Passkey name"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              helperText="Helps you tell your passkeys apart, such as “Work laptop”."
              inputProps={{ maxLength: PASSKEY_LABEL_MAX_LENGTH }}
              variant="outlined"
              disabled={busy}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy} size="large">
            Cancel
          </Button>
          <Button color="primary" size="large" type="submit" disabled={busy}>
            Create passkey
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
